/**
 * AutoExposure — average-log-luminance eye adaptation (UE "Auto Exposure
 * Basic" / Narkowicz "Automatic Exposure" family).
 *
 * Measurement: small center-weighted regions of the post pipeline's HDR scene
 * pass render target are read back asynchronously (HalfFloat → decoded on
 * CPU), and the geometric mean of luminance (average of log2) is computed —
 * log averaging keeps a bright sky from crushing a dark interior the way an
 * arithmetic mean does. The scene's log-average is treated as middle grey
 * (the photographic 18% convention): target exposure = key / avgLum.
 *
 * Adaptation: exposure eases toward the target with separate up (dark→bright
 * reacts faster) and down speeds via 1 − exp(−dt·speed) — frame-rate
 * independent. The result multiplies a base exposure and is written to
 * `renderer.toneMappingExposure`, which three's PostProcessing output
 * transform consumes.
 *
 * The GPU histogram variant (64-bin compute + percentile clipping) is a
 * documented v2; this readback path works identically on WebGL2 and WebGPU.
 */

import type * as THREE from 'three/webgpu';
import { Logger } from '../../core/Logger.js';
import { LoopPhase, SubPhase } from '../../shared/types.js';
import type { ISystem } from '../../ecs/System.js';
import type { World } from '../../ecs/World.js';

// ─── CPU math (exported for tests) ──────────────────────────────────────────

/** Decode an IEEE 754 half-precision value from its raw uint16 bits. */
export function halfToFloat(bits: number): number {
  const sign = (bits & 0x8000) ? -1 : 1;
  const exp = (bits >> 10) & 0x1f;
  const frac = bits & 0x3ff;
  if (exp === 0) return sign * frac * 2 ** -24;               // subnormal
  if (exp === 31) return frac ? NaN : sign * Infinity;        // inf / nan
  return sign * (1 + frac / 1024) * 2 ** (exp - 15);
}

/** Rec. 709 relative luminance. */
export function luminance709(r: number, g: number, b: number): number {
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Weighted average of log2 luminance over RGBA pixel blocks. Non-finite and
 * near-zero samples are floored (a single NaN or true-black texel must not
 * poison the mean — the same lesson as the env-map NaN bug).
 */
export function averageLogLuminance(
  blocks: Array<{ pixels: Float32Array; weight: number }>,
): number {
  let sum = 0;
  let wsum = 0;
  for (const { pixels, weight } of blocks) {
    let blockSum = 0;
    let n = 0;
    for (let i = 0; i + 3 < pixels.length; i += 4) {
      let lum = luminance709(pixels[i], pixels[i + 1], pixels[i + 2]);
      if (!Number.isFinite(lum) || lum < 1e-6) lum = 1e-6;
      blockSum += Math.log2(lum);
      n++;
    }
    if (n > 0) {
      sum += (blockSum / n) * weight;
      wsum += weight;
    }
  }
  if (wsum === 0) return Math.log2(0.18);
  return sum / wsum;
}

/** Target exposure multiplier for a measured average luminance. */
export function targetExposureForLuminance(
  avgLuminance: number, key: number, minExposure: number, maxExposure: number,
): number {
  const e = key / Math.max(avgLuminance, 1e-6);
  return Math.min(maxExposure, Math.max(minExposure, e));
}

/** Frame-rate-independent exponential ease toward the target. */
export function adaptExposure(
  current: number, target: number, dt: number, speedUp: number, speedDown: number,
): number {
  const speed = target > current ? speedUp : speedDown;
  return current + (target - current) * (1 - Math.exp(-dt * speed));
}

// ─── Histogram metering (2026-09-15, shader additions wave) ─────────────────
//
// A log-average is dragged by outliers: one sun disc or one black doorway
// swings the whole frame. UE-style histogram metering accumulates log2
// luminance into bins, trims a low and a high PERCENTILE of the pixel
// population, and averages what is left — so a few very bright or very dark
// pixels cannot own the exposure decision. Bins are over the metered SAMPLES
// (the same centre-weighted regions the average reads), not a full-frame GPU
// histogram; the maths is identical, the population smaller.

export const HISTOGRAM_MIN_LOG = -10;
export const HISTOGRAM_MAX_LOG = 10;

/** Weighted log2-luminance histogram over the metered blocks. */
export function histogramLogLuminance(
  blocks: Array<{ pixels: Float32Array; weight: number }>, bins = 64,
  minLog = HISTOGRAM_MIN_LOG, maxLog = HISTOGRAM_MAX_LOG,
): Float32Array {
  const hist = new Float32Array(Math.max(2, bins));
  const span = Math.max(1e-6, maxLog - minLog);
  for (const { pixels, weight } of blocks) {
    for (let i = 0; i + 2 < pixels.length; i += 4) {
      const lum = luminance709(pixels[i], pixels[i + 1], pixels[i + 2]);
      const l = Math.log2(Math.max(lum, 1e-6));
      let b = Math.floor(((l - minLog) / span) * hist.length);
      if (b < 0) b = 0; else if (b >= hist.length) b = hist.length - 1;
      hist[b] += weight;
    }
  }
  return hist;
}

/**
 * Mean log2 luminance of the bins between the low and high percentiles of the
 * population (UE defaults: 50 %..98 % — the shadows and the very brightest are
 * trimmed). Returns 0 (= luminance 1) for an empty histogram.
 */
export function percentileLogLuminance(
  hist: Float32Array, lowPct = 0.5, highPct = 0.98,
  minLog = HISTOGRAM_MIN_LOG, maxLog = HISTOGRAM_MAX_LOG,
): number {
  let total = 0;
  for (let i = 0; i < hist.length; i++) total += hist[i];
  if (total <= 0) return 0;
  const lo = Math.min(lowPct, highPct) * total, hi = Math.max(lowPct, highPct) * total;
  const binW = (maxLog - minLog) / hist.length;
  let acc = 0, sum = 0, w = 0;
  for (let i = 0; i < hist.length; i++) {
    const c = hist[i];
    const from = acc, to = acc + c;
    acc = to;
    if (to <= lo || from >= hi) continue;
    const part = Math.min(to, hi) - Math.max(from, lo);   // the slice of this bin inside the band
    if (part <= 0) continue;
    const centre = minLog + (i + 0.5) * binW;
    sum += centre * part; w += part;
  }
  return w > 0 ? sum / w : 0;
}

/**
 * Exponential adaptation in LOG exposure (stops), with separate time
 * constants for brightening and darkening — the frame-rate-independent form
 * the guide states: a = 1 − e^(−dt/τ). `stops` is log2 of the exposure
 * multiplier, NOT photographic EV100 (whose sign runs the other way).
 */
export function adaptLogExposure(
  previousStops: number, targetStops: number, dtSeconds: number, brightenTauSeconds: number, darkenTauSeconds: number,
): number {
  const tau = targetStops > previousStops ? brightenTauSeconds : darkenTauSeconds;
  if (tau <= 0) return targetStops;
  const a = -Math.expm1(-Math.max(0, dtSeconds) / tau);
  return previousStops + a * (targetStops - previousStops);
}

// ─── Controller ─────────────────────────────────────────────────────────────

export interface AutoExposureConfig {
  /** Middle-grey key value (default 0.18). */
  key?: number;
  /** Exposure multiplier clamp (defaults 0.25 / 4). */
  minExposure?: number;
  maxExposure?: number;
  /** Adaptation speeds, 1/s (defaults: up 3, down 1 — eyes brighten faster). */
  speedUp?: number;
  speedDown?: number;
  /** Frames between measurements (default 10 ≈ 6 Hz at 60 fps). */
  intervalFrames?: number;
  /** Base exposure the adaptation multiplies (default: renderer's at init). */
  baseExposure?: number;
  /** Metering: the log average, or the percentile-trimmed histogram (default 'average'). */
  metering?: 'average' | 'histogram';
  /** Histogram trims: keep the population between these percentiles (defaults 0.5 / 0.98). */
  lowPercentile?: number;
  highPercentile?: number;
  histogramBins?: number;
  /**
   * Adaptation strength 0..1 (default 1): blends the smoothed adaptation
   * toward neutral — applied = base·(1 + (adaptation − 1)·strength). 0 pins
   * exposure at base (metering still runs); 1 is full eye adaptation.
   */
  strength?: number;
  /**
   * THE ANCHORED FORM (2026-09-25, Atlas realism §8 R3 / §10 R2). When set
   * (`setAnchor`), the controller no longer free-runs toward key/avgLum: it
   * meters a CORRECTION in stops around the caller's exposure curve, clamped
   * to ±correctionRangeEv and eased in LOG space with separate brighten /
   * darken time constants. Caves and interiors adapt; a night is never lifted
   * to grey day (the curve owns the hour; the metering only nudges).
   */
  correctionRangeEv?: number;
  /**
   * The DARKENING limit of the anchored correction in stops (2026-09-26); a
   * negative value (the default) means the same as `correctionRangeEv`. An
   * honest meter over an outdoor frame reads the sky and the haze, not the
   * ground: Atlas measured a clear-noon landscape 2 stops over its own ground
   * (the bright horizon) and a storm frame 1.4 stops under it, so a symmetric
   * range either darkened every clear view or lifted every storm. With the
   * bias on the ground and a small darkening limit the correction brightens
   * the dark (caves, interiors, deep shade) and barely darkens the bright.
   */
  correctionDarkenEv?: number;
  /** Anchored easing time constants in seconds (defaults 0.4 up / 1.2 down — eyes brighten faster). */
  brightenTau?: number;
  darkenTau?: number;
}

/**
 * The anchored correction for one reading: the stops that bring the metered
 * DISPLAYED key (scene luminance × curve exposure) to `key`, clamped.
 */
export function anchoredCorrectionStops(meteredLuminance: number, curveExposure: number, key: number, rangeEv: number, darkenEv = rangeEv): number {
  const displayed = Math.max(1e-6, meteredLuminance * Math.max(1e-9, curveExposure));
  const want = Math.log2(Math.max(1e-9, key) / displayed);
  return Math.max(-(darkenEv >= 0 ? darkenEv : rangeEv), Math.min(rangeEv, want));
}

interface MeterRegion { cx: number; cy: number; size: number; weight: number }

/**
 * Center-weighted 5-region metering pattern (fractions of the RT).
 *
 * THE READBACK LAW (2026-09-26): every region is 32 px. three pads a WebGPU
 * readback's rows to 256 bytes (`WebGPUTextureUtils.copyTextureToBuffer`) and
 * returns the padded buffer, so a 16-px RGBA16F region (128-byte rows) came
 * back with HALF of its texels the fresh buffer's zeros; the log-average was
 * dragged ~5 stops down and the anchored correction pegged at +range — the
 * noon whiteout. A 32-texel RGBA16F row is exactly 256 bytes (RGBA32F 512):
 * no padding on either backend. `decodeMeterBlock` still walks a padded
 * stride, and the corner weights keep the old 80/20 centre weighting
 * (4·1024 vs 4·0.25·1024 — the corners were 16² at weight 1).
 */
const METER_REGIONS: MeterRegion[] = [
  { cx: 0.5, cy: 0.5, size: 32, weight: 4 },
  { cx: 0.25, cy: 0.3, size: 32, weight: 0.25 },
  { cx: 0.75, cy: 0.3, size: 32, weight: 0.25 },
  { cx: 0.25, cy: 0.7, size: 32, weight: 0.25 },
  { cx: 0.75, cy: 0.7, size: 32, weight: 0.25 },
];

/** Readings in a row that returned no usable block before the correction goes neutral. */
export const METER_BLIND_AFTER = 3;

/**
 * Decode one region readback into tightly packed RGBA floats. Walks the
 * padded rows three's WebGPU readback returns (`raw` longer than s·s·4, each
 * row a stride apart — the buffer is sized (h − 1)·stride + w·4) and REJECTS
 * a block that carries no picture: every RGB texel exactly 0 (a copy that
 * never landed) or more than 5 % non-finite texels (garbage). Returns null
 * for such a block so the meter drops it instead of averaging it; a stray
 * NaN texel inside a real picture is kept (the averagers floor it).
 */
export function decodeMeterBlock(raw: ArrayLike<number> & { constructor: { name: string } }, s: number): Float32Array | null {
  const isHalf = raw.constructor.name === 'Uint16Array';
  const rowElems = s * 4;
  const want = s * rowElems;
  if (s <= 0 || raw.length < want) return null;
  const stride = s > 1 && raw.length > want ? Math.floor((raw.length - rowElems) / (s - 1)) : rowElems;
  const out = new Float32Array(want);
  let anyLight = false;
  let bad = 0;
  for (let row = 0; row < s; row++) {
    const base = row * stride;
    const o = row * rowElems;
    for (let i = 0; i < rowElems; i++) {
      const v = raw[base + i];
      const f = isHalf ? halfToFloat(v) : v;
      out[o + i] = f;
      if ((i & 3) === 3) continue;
      if (!Number.isFinite(f)) bad++;
      else if (f !== 0) anyLight = true;
    }
  }
  if (!anyLight || bad * 20 > s * s * 3) return null;
  return out;
}

export class AutoExposureController {
  readonly config: Required<AutoExposureConfig>;
  private _adaptation = 1;
  private _target = 1;
  private _inFlight = false;
  /** Consecutive readings that produced no usable block (`METER_BLIND_AFTER` → neutral). */
  private _failures = 0;
  private _warnedBlind = false;
  /** Readings in a row with no usable block (0 after any successful reading). */
  get blindReadings(): number { return this._failures; }

  constructor(config?: AutoExposureConfig, rendererExposure = 1) {
    this.config = {
      key: config?.key ?? 0.18,
      minExposure: config?.minExposure ?? 0.25,
      maxExposure: config?.maxExposure ?? 4,
      speedUp: config?.speedUp ?? 3,
      speedDown: config?.speedDown ?? 1,
      intervalFrames: config?.intervalFrames ?? 10,
      baseExposure: config?.baseExposure ?? rendererExposure,
      strength: config?.strength ?? 1,
      metering: config?.metering ?? 'average',
      lowPercentile: config?.lowPercentile ?? 0.5,
      highPercentile: config?.highPercentile ?? 0.98,
      histogramBins: config?.histogramBins ?? 64,
      correctionRangeEv: config?.correctionRangeEv ?? 1.5,
      correctionDarkenEv: config?.correctionDarkenEv ?? -1,
      brightenTau: config?.brightenTau ?? 0.4,
      darkenTau: config?.darkenTau ?? 1.2,
    };
  }

  // ── Anchored form state ──
  private _anchor: number | null = null;
  private _anchorKey = 0.18;
  private _targetStops = 0;
  private _stops = 0;
  private _meteredLog = Number.NaN;
  /** The last reading's metered scene luminance (the HDR target's units; NaN before the first) — the meter-bias calibration reads it. */
  get meteredLuminance(): number { return 2 ** this._meteredLog; }
  /**
   * Anchor the metering to a curve exposure (the value the caller applies
   * without metering) and the displayed key it wants at this hour; null
   * returns to the free-running form. Call every frame — cheap.
   */
  setAnchor(curveExposure: number | null, key = this.config.key): void {
    this._anchor = curveExposure;
    this._anchorKey = key;
  }
  /** Anchored: the smoothed correction in stops (0 when not anchored). */
  get correctionStops(): number { return this._anchor === null ? 0 : this._stops; }
  /** Anchored: the latest measured (unsmoothed) correction in stops. */
  get targetStops(): number { return this._targetStops; }
  /** Anchored: advance the log-space ease; returns the correction in stops (× strength). */
  updateAnchored(dt: number): number {
    this._stops = adaptLogExposure(this._stops, this._targetStops, dt, this.config.brightenTau, this.config.darkenTau);
    return this._stops * this.config.strength;
  }

  /** Switch the metering live (no re-measure needed; the next reading uses it). */
  setMetering(m: 'average' | 'histogram'): void { this.config.metering = m; }

  /** Live adaptation-strength knob (0 = pinned at base, 1 = full). */
  setStrength(v: number): void {
    this.config.strength = Math.max(0, Math.min(1, v));
  }

  /** Current smoothed adaptation multiplier. */
  get adaptation(): number { return this._adaptation; }
  /** Latest measured (unsmoothed) target. */
  get target(): number { return this._target; }
  get measuring(): boolean { return this._inFlight; }

  /**
   * Kick an async measurement from the HDR scene render target. Resolves when
   * the readbacks land; concurrent calls while one is in flight are no-ops
   * (the FeedbackReadback in-flight-guard pattern).
   */
  async measure(renderer: THREE.WebGPURenderer, rt: { width: number; height: number }): Promise<void> {
    if (this._inFlight) return;
    const read = (renderer as unknown as {
      readRenderTargetPixelsAsync?: (
        rt: unknown, x: number, y: number, w: number, h: number,
      ) => Promise<ArrayLike<number> & { constructor: { name: string } }>;
    }).readRenderTargetPixelsAsync;
    if (!read) return;
    this._inFlight = true;
    try {
      // THE ONE-FRAME LAW (2026-09-26): the five copies are ENCODED IN ONE JS TURN against the same frame's
      // target. Awaited one after another (the previous form) a reading spanned five frames — five readback
      // round trips of latency, and under a slow renderer a picture that changed between the first copy and the
      // last (a black region beside a lit one, averaged as one frame).
      const reads = METER_REGIONS.map(async (region) => {
        const s = Math.min(region.size, rt.width, rt.height);
        const x = Math.max(0, Math.min(rt.width - s, Math.round(region.cx * rt.width - s / 2)));
        const y = Math.max(0, Math.min(rt.height - s, Math.round(region.cy * rt.height - s / 2)));
        const raw = await read.call(renderer, rt, x, y, s, s);
        const pixels = decodeMeterBlock(raw, s);
        return pixels ? { pixels, weight: region.weight } : null;
      });
      const blocks = (await Promise.all(reads)).filter((b): b is { pixels: Float32Array; weight: number } => b !== null);
      if (blocks.length === 0) throw new Error('no usable meter block');
      const avgLog = this.config.metering === 'histogram'
        ? percentileLogLuminance(histogramLogLuminance(blocks, this.config.histogramBins), this.config.lowPercentile, this.config.highPercentile)
        : averageLogLuminance(blocks);
      this._meteredLog = avgLog;
      this._target = targetExposureForLuminance(
        2 ** avgLog, this.config.key, this.config.minExposure, this.config.maxExposure,
      );
      if (this._anchor !== null) {
        this._targetStops = anchoredCorrectionStops(2 ** avgLog, this._anchor, this._anchorKey, this.config.correctionRangeEv, this.config.correctionDarkenEv);
      }
      this._failures = 0;
    } catch {
      // A failed read (context loss, resize race, an empty copy) keeps the previous target for a reading or two;
      // METER_BLIND_AFTER failures in a row declare the meter BLIND and the anchored correction goes NEUTRAL (0)
      // rather than holding a stale correction against the curve for the rest of the session (2026-09-26: a
      // held reading washed out every later hour). The free-running target keeps its last value (its own clamp).
      if (++this._failures >= METER_BLIND_AFTER) {
        this._targetStops = 0;
        this._meteredLog = Number.NaN;
        if (!this._warnedBlind) {
          this._warnedBlind = true;
          Logger.warn('AutoExposure', `metering readback returned no usable pixels ${METER_BLIND_AFTER} times running — correction held at 0 until it reads again`);
        }
      }
    } finally {
      this._inFlight = false;
    }
  }

  /** Advance adaptation and return the exposure to apply. */
  update(dt: number): number {
    this._adaptation = adaptExposure(
      this._adaptation, this._target, dt, this.config.speedUp, this.config.speedDown,
    );
    // strength blends toward neutral so a UI slider can soften the effect
    // without stopping the metering (re-raising it responds instantly).
    return this.config.baseExposure * (1 + (this._adaptation - 1) * this.config.strength);
  }
}

// ─── System ─────────────────────────────────────────────────────────────────

/**
 * Post-phase system: measures every `intervalFrames`, eases every frame, and
 * writes `renderer.toneMappingExposure`. The render-target provider is a
 * closure so the system stays decoupled from PostProcessingPipeline's
 * internals (pass `() => pipeline.scenePassRenderTarget`).
 */
export class AutoExposureSystem implements ISystem {
  readonly name = 'AutoExposure';
  readonly phase = LoopPhase.Post;
  readonly subPhase = SubPhase.Update;
  readonly priority = 5;

  readonly controller: AutoExposureController;
  private readonly _renderer: THREE.WebGPURenderer;
  private readonly _getTarget: () => { width: number; height: number } | null;
  private _frame = 0;

  constructor(args: {
    renderer: THREE.WebGPURenderer;
    getRenderTarget: () => { width: number; height: number } | null;
    config?: AutoExposureConfig;
  }) {
    this._renderer = args.renderer;
    this._getTarget = args.getRenderTarget;
    this.controller = new AutoExposureController(
      args.config, args.renderer.toneMappingExposure,
    );
  }

  execute(dt: number, _world: World): void {
    this._frame++;
    if (this._frame % this.controller.config.intervalFrames === 0) {
      const rt = this._getTarget();
      // XR guard: mid-session readbacks stall the frame budget — adaptation
      // freezes at its last value while presenting.
      const xr = (this._renderer as unknown as { xr?: { isPresenting?: boolean } }).xr;
      if (rt && !xr?.isPresenting) {
        void this.controller.measure(this._renderer, rt);
      }
    }
    this._renderer.toneMappingExposure = this.controller.update(dt);
  }

  dispose(): void { /* no GPU resources owned */ }
}
